import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  const requireEmailVerification = useFeatureFlag('require_email_verified_for_app_access');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Preserve the intended destination so LoginPage can redirect back after auth
    const next = location.pathname !== '/login' ? `?next=${encodeURIComponent(location.pathname)}` : '';
    return <Navigate to={`/login${next}`} replace />;
  }

  // Phase SEC-A: Check email verification requirement
  // Allow access to verify-email-required page without email verification
  if (
    requireEmailVerification &&
    user &&
    !user.is_verified &&
    location.pathname !== '/verify-email-required'
  ) {
    return <Navigate to="/verify-email-required" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
